import React, { useState } from "react";
import ReactTable from "react-table";
import DeportivaEventoAsignable from "../../../types/DeportivaEventoAsignable";
import { Button, Badge } from "reactstrap";
import { Archivo } from "../expedientsV2controls/types";
import ArchivoControl from "../expedientsV2controls/archivo/archivo";
import { formatIsoDate } from "../../helpers/formatters";
import {
  faCheck,
  faCheckCircle,
  faCheckDouble,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import BaseButtonWithIconAndTooltip from "../../../toolkit/baseButtonWithIconAndTooltip";
import { programaCedulaFormConfig } from "./ComisionesDeportivaAppGestionProgramaCedula.form";
import { programaReglamentoFormConfig } from "./ComisionesDeportivaAppGestionProgramaReglamento.form";
import { Preview } from "../../../types/IAction";
import s from "../comisionesDeportivaV2Carrusel/styles.scss";
import RegenerarCincelSm from "../expedientsV2controls/archivo/regenerarCincelSm";

interface Props {
  data: DeportivaEventoAsignable[];
  loading: boolean;
  onChangeInstructor: (row: DeportivaEventoAsignable) => void;
  onVerificarDocumentacion: (row: DeportivaEventoAsignable) => void;
  onRegenerar: (
    row: DeportivaEventoAsignable,
    archivo: Archivo,
    values: any
  ) => Promise<Preview<any>>;
  onCincelFirma: (
    row: DeportivaEventoAsignable,
    archivo: Archivo
  ) => Promise<Preview<any>>;
}

const columnsFormatter = (
  onChangeInstructor: (row: DeportivaEventoAsignable) => void,
  onVerificarDocumentacion: (row: DeportivaEventoAsignable) => void,
  onRegenerar: (
    row: DeportivaEventoAsignable,
    archivo: Archivo,
    values: any
  ) => Promise<Preview<any>>,
  onCincelFirma: (
    row: DeportivaEventoAsignable,
    archivo: Archivo
  ) => Promise<Preview<any>>
) => [
  {
    Header: "Factura",
    headerClassName: "text-left",
    accessor: "folioFactura",
    width: 96
  },
  {
    Header: "Producto",
    headerClassName: "text-left",
    accessor: "producto"
  },
  {
    Header: "Cliente",
    headerClassName: "text-left",
    accessor: "nombrePersona"
  },
  {
    Header: "Club",
    headerClassName: "text-left",
    accessor: "clubVenta",
    width: 128
  },
  {
    Header: "Venta",
    headerClassName: "text-left",
    accessor: "fechaVenta",
    width: 96,
    Cell: (row: any) => formatIsoDate(row.value)
  },
  {
    Header: "Horario sugerido",
    headerClassName: "text-left",
    width: 128,
    Cell: (row: any) => {
      const { fechaSugerida, horaSugerida } = row.original;
      if (fechaSugerida && horaSugerida) {
        return `${formatIsoDate(fechaSugerida)} ${horaSugerida}`;
      }
      return "-";
    }
  },
  {
    Header: "Cédula",
    headerClassName: "text-left",
    width: 64,
    Cell: (row: any) => {
      const evento: DeportivaEventoAsignable = row.original;
      return documentAccessor(
        onRegenerar,
        onCincelFirma,
        evento,
        evento.cedulaProgramaDeportivo
      );
    }
  },
  {
    Header: "Regmto.",
    headerClassName: "text-left",
    width: 64,
    Cell: (row: any) => {
      const evento: DeportivaEventoAsignable = row.original;
      const archivo =
        evento.reglamentoProgramaDeportivo ||
        evento.reglamentoClasePersonalizada;
      return documentAccessor(onRegenerar, onCincelFirma, evento, archivo);
    }
  },
  {
    Header: "",
    headerClassName: "text-left",
    width: 64,
    Cell: (row: any) => {
      const evento: DeportivaEventoAsignable = row.original;
      const id = `id-${evento.idProductoInstancia}`;
      const esperaInstructor = evento.esperaInstructor;
      const esperaDocumentacion = evento.esperaDocumentacion;

      return (
        <div>
          {esperaInstructor && (
            <BaseButtonWithIconAndTooltip
              size="sm"
              id={`${id}-asignar-instructor`}
              icon={faUser}
              tooltipTitle="Asignar instructor"
              onClick={() => onChangeInstructor(evento)}
            />
          )}
          {esperaDocumentacion && !esperaInstructor && (
            <BaseButtonWithIconAndTooltip
              size="sm"
              id={`${id}-firma-instructor`}
              icon={faCheckCircle}
              tooltipTitle="Verificar firmas de instructor"
              onClick={() => onVerificarDocumentacion(evento)}
            />
          )}
        </div>
      );
    }
  }
];

export const MemoTable = React.memo<Props>(
  ({
    data,
    loading,
    onChangeInstructor,
    onVerificarDocumentacion,
    onRegenerar,
    onCincelFirma
  }) => {
    const [columns] = useState(
      columnsFormatter(
        onChangeInstructor,
        onVerificarDocumentacion,
        onRegenerar,
        onCincelFirma
      )
    );

    return (
      <div style={{ margin: "0 -15px", width: "100%" }}>
        <ReactTable
          data={data}
          columns={columns}
          loading={loading}
          showPagination={false}
          pageSize={data && data.length > 5 ? data.length : 5}
          minRows={5}
          className="-striped -highlight"
          noDataText="No hay ventas pendientes"
          loadingText="Cargando..."
        />
      </div>
    );
  }
);

function documentAccessor(
  onRegenerar: (
    row: DeportivaEventoAsignable,
    archivo: Archivo,
    values: any
  ) => Promise<Preview<any>>,
  onCincelFirma: (
    row: DeportivaEventoAsignable,
    archivo: Archivo
  ) => Promise<Preview<any>>,
  row: DeportivaEventoAsignable,
  archivo: Archivo
) {
  if (!archivo) {
    return <div>&nbsp;</div>;
  }

  let docForm = null;
  if (archivo.codigo === "CINCEL_CEDULA_PROGRAMA") {
    docForm = programaCedulaFormConfig;
  } else if (archivo.codigo === "CINCEL_PROGRAMA_REGLAMENTO") {
    docForm = programaReglamentoFormConfig;
  }

  const estiloFirmado = archivo.completo
    ? s.documentoFirmado
    : s.documentoNoFirmado;

  return (
    <div className={estiloFirmado}>
      <RegenerarCincelSm
        key={"archivo-item-" + archivo.idExpediente + "-" + archivo.idItem}
        archivo={archivo}
        userId={0}
        botonCorreo={false}
        botonRegenrar={true}
        formConfigRegenerar={docForm}
        regenerar={(archivo: Archivo, values?: any) => {
          return onRegenerar(row, archivo, values);
        }}
        cincelFirma={archivoResp => onCincelFirma(row, archivoResp)}
      />
    </div>
  );
}
